<!-- Loading bar -->
<fuse-loading-bar></fuse-loading-bar>

<!-- Navigation -->
<fuse-vertical-navigation
    class="dark print:hidden"
    [appearance]="navigationAppearance"
    [mode]="isScreenSmall ? 'over' : 'side'"
    [name]="'mainNavigation'"
    [navigation]="navigation?.default || []"
    [opened]="!isScreenSmall"
>
    <!-- Navigation header hook -->
    <ng-container fuseVerticalNavigationContentHeader>
        <!-- Logo -->
        <div class="flex h-20 items-center justify-center px-2">
            <!-- Single/icon logo for collapsed/dense state (dark bg) -->
            <img
                class="dense-logo-single w-10"
                [src]="
                    tenantConfig.getConfig().branding.iconUrl ||
                    tenantConfig.getConfig().branding.faviconUrl ||
                    tenantConfig.getConfig().branding.logoDarkUrl ||
                    tenantConfig.getConfig().branding.logoUrl
                "
                [alt]="tenantConfig.getConfig().identity.displayName"
            />
            <!-- Full logo with text for expanded/hover state (dark bg) -->
            <img
                class="dense-logo-full w-3/4"
                [src]="
                    tenantConfig.getConfig().branding.logoDarkUrl ||
                    tenantConfig.getConfig().branding.logoUrl
                "
                [alt]="tenantConfig.getConfig().identity.displayName"
            />
        </div>
    </ng-container>
</fuse-vertical-navigation>

<!-- Wrapper -->
<div class="flex w-full min-w-0 flex-auto flex-col">
    <!-- Header -->
    <div
        class="relative z-49 flex h-16 w-full flex-0 items-center border-b border-gray-200 bg-white px-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 md:px-6 print:hidden"
    >
        <div class="flex items-center space-x-2 pr-2">
            <!-- Navigation toggle button -->
            <button
                mat-icon-button
                (click)="toggleNavigation('mainNavigation')"
            >
                <mat-icon [svgIcon]="'heroicons_outline:bars-3'"></mat-icon>
            </button>
            <!-- Navigation appearance toggle button -->
            <button
                class="hidden md:inline-flex"
                mat-icon-button
                (click)="toggleNavigationAppearance()"
            >
                <mat-icon
                    [svgIcon]="'heroicons_outline:arrows-right-left'"
                ></mat-icon>
            </button>
        </div>
        <!-- Components -->
        <div class="ml-auto flex items-center space-x-2 pl-2 md:space-x-4">
            @if (user?.otherRoles?.includes(RoleList.FilmMaker)) {
                <!-- Current Level Display — hidden when level feature is off -->
                @if (features.level) {
                    <div
                        class="flex items-center space-x-1 sm:space-x-2"
                        [matTooltip]="
                            ('dashboard.level' | translate) +
                            ': ' +
                            (currentLevel?.name || 'Loading...')
                        "
                        matTooltipPosition="below"
                    >
                        <div
                            class="flex h-6 w-6 items-center justify-center rounded-full bg-teal-200 dark:bg-teal-900/20 md:h-8 md:w-8"
                        >
                            <mat-icon
                                class="text-sm text-green-800 dark:text-blue-400 md:text-base"
                                >flag</mat-icon
                            >
                        </div>
                        @if (isLoadingLevel) {
                            <div class="flex items-center space-x-1">
                                <div
                                    class="h-3 w-3 animate-spin rounded-full border border-gray-400 border-t-transparent"
                                ></div>
                            </div>
                        } @else {
                            <div class="flex flex-col">
                                <span
                                    class="text-xs font-medium text-gray-500 dark:text-gray-400 md:text-sm"
                                    >{{
                                        'dashboard.currentLevel' | translate
                                    }}</span
                                >
                                <span
                                    class="text-xs font-bold text-gray-900 dark:text-white md:text-lg"
                                    >{{ currentLevel?.name || 'N/A' }}</span
                                >
                            </div>
                        }
                    </div>

                    <!-- Divider after level (only when cash or reels follows) -->
                    @if (features.cash || features.reels) {
                        <div
                            class="h-8 w-px bg-gray-200 dark:bg-gray-700"
                        ></div>
                    }
                }

                <!-- Wallet Balance Display — hidden when cash feature is off -->
                @if (features.cash) {
                    <button
                        type="button"
                        class="appearance-none flex cursor-pointer items-center space-x-1 border-0 bg-transparent p-0 sm:space-x-2"
                        (click)="navigateToWallet('cash')"
                        [matTooltip]="'tooltip.wallet' | translate"
                        matTooltipPosition="below"
                    >
                        <div
                            class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20 md:h-8 md:w-8"
                        >
                            <mat-icon
                                class="text-sm text-green-600 dark:text-green-400 md:text-base"
                                >account_balance_wallet</mat-icon
                            >
                        </div>
                        @if (isLoadingWallet) {
                            <div class="flex items-center space-x-1">
                                <div
                                    class="h-3 w-3 animate-spin rounded-full border border-gray-400 border-t-transparent"
                                ></div>
                            </div>
                        } @else {
                            <div class="flex flex-col">
                                <span
                                    class="text-xs font-medium text-gray-500 dark:text-gray-400 md:text-sm"
                                    >{{ 'dashboard.cash' | translate }}</span
                                >
                                <span
                                    class="text-xs font-bold text-gray-900 dark:text-white md:text-lg"
                                    >{{ currencySymbol + walletBalance }}</span
                                >
                            </div>
                        }
                    </button>

                    <!-- Divider after cash (only when reels follows) -->
                    @if (features.reels) {
                        <div
                            class="h-8 w-px bg-gray-200 dark:bg-gray-700"
                        ></div>
                    }
                }

                <!-- Reel Balance Display — hidden when reels feature is off -->
                @if (features.reels) {
                    <button
                        type="button"
                        class="appearance-none flex cursor-pointer items-center space-x-1 border-0 bg-transparent p-0 sm:space-x-2"
                        (click)="navigateToWallet('reels')"
                        [matTooltip]="'tooltip.reels' | translate"
                        matTooltipPosition="below"
                    >
                        <div
                            class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/20 md:h-8 md:w-8"
                        >
                            <mat-icon
                                class="text-sm text-purple-600 dark:text-purple-400 md:text-base"
                                >movie</mat-icon
                            >
                        </div>
                        @if (isLoadingReel) {
                            <div class="flex items-center space-x-1">
                                <div
                                    class="h-3 w-3 animate-spin rounded-full border border-gray-400 border-t-transparent"
                                ></div>
                            </div>
                        } @else {
                            <div class="flex flex-col">
                                <span
                                    class="text-xs font-medium text-gray-500 dark:text-gray-400 md:text-sm"
                                    >{{ 'dashboard.reels' | translate }}</span
                                >
                                <span
                                    class="text-xs font-bold text-gray-900 dark:text-white md:text-lg"
                                    >{{ reelBalance }}</span
                                >
                            </div>
                        }
                    </button>
                }

                <!-- Divider before language selector (only when at least one balance block is shown) -->
                @if (features.level || features.cash || features.reels) {
                    <div class="h-8 w-px bg-gray-200 dark:bg-gray-700"></div>
                }

                <!-- Language Selector -->
                <div class="hidden lg:block">
                    <app-language-selector></app-language-selector>
                </div>
            }
            @if (!user?.otherRoles?.includes(RoleList.FilmMaker)) {
                <p>{{ date | date: 'd MMM, y' }}</p>
            }
            <fuse-fullscreen class="hidden lg:block"></fuse-fullscreen>
            @if (askTalkieEnabled) {
                <button
                    type="button"
                    class="talkie-trigger"
                    (click)="toggleAskTalkie()"
                    [attr.aria-label]="'askTalkie.triggerAria' | translate"
                >
                    <span class="talkie-trigger__glyph" aria-hidden="true">
                        <mat-icon
                            class="text-white icon-size-5"
                            [svgIcon]="
                                'heroicons_outline:chat-bubble-left-right'
                            "
                        ></mat-icon>
                    </span>
                    <span class="talkie-trigger__copy">
                        <span class="talkie-trigger__title">{{
                            'askTalkie.title' | translate
                        }}</span>
                        <span class="talkie-trigger__hint">{{
                            'askTalkie.triggerHint' | translate
                        }}</span>
                    </span>
                </button>
            }
            <div class="flex items-center">
                <user></user>
            </div>
        </div>
    </div>

    <!-- Content -->
    <div class="flex flex-auto flex-col">
        <!-- *ngIf="true" hack is required here for router-outlet to work correctly.
             Otherwise, layout changes won't be registered and the view won't be updated! -->
        @if (true) {
            <router-outlet></router-outlet>
        }
    </div>

    @if (askTalkieEnabled) {
        <slate-panel></slate-panel>
    }

    <!-- Footer -->
    <div
        class="bg-card relative z-49 flex w-full flex-0 flex-col items-center gap-2 border-t px-4 py-3 dark:bg-transparent md:flex-row md:justify-between md:gap-x-4 md:gap-y-1 md:px-6 print:hidden"
    >
        <div class="text-center md:text-left">
            <span class="text-sm"
                >&copy; {{ currentYear }}
                {{ tenantConfig.getConfig().identity.legalName }}</span
            >
        </div>
        <div
            class="flex flex-wrap items-center justify-center gap-2 dark:bg-transparent print:hidden"
        >
            @if (tenantConfig.getConfig().footerLinks.termsUrl) {
                <a
                    [href]="tenantConfig.getConfig().footerLinks.termsUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-secondary text-sm hover:text-primary hover:underline"
                    >Terms &amp; Conditions</a
                >
                <span class="text-secondary text-sm">&middot;</span>
            }
            @if (tenantConfig.getConfig().footerLinks.privacyUrl) {
                <a
                    [href]="
                        tenantConfig.getConfig().footerLinks.privacyUrl?.trim()
                    "
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-secondary text-sm hover:text-primary hover:underline"
                    >Privacy Policy</a
                >
                <span class="text-secondary text-sm">&middot;</span>
            }
            @if (tenantConfig.getConfig().footerLinks.refundUrl) {
                <a
                    [href]="tenantConfig.getConfig().footerLinks.refundUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-secondary text-sm hover:text-primary hover:underline"
                    >Payment &amp; Refund Policy</a
                >
                <span class="text-secondary text-sm">&middot;</span>
            }
            @if (tenantConfig.getConfig().footerLinks.contactUrl) {
                <a
                    [href]="tenantConfig.getConfig().footerLinks.contactUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-secondary text-sm hover:text-primary hover:underline"
                    >Contact Us</a
                >
                <span class="text-secondary text-sm">&middot;</span>
            }
            @if (tenantConfig.getConfig().footerLinks.websiteUrl) {
                <a
                    [href]="tenantConfig.getConfig().footerLinks.websiteUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-secondary text-sm hover:text-primary hover:underline"
                    >Website</a
                >
            }
        </div>
        <div class="text-center md:text-right">
            @if (tenantConfig.getConfig().footerLinks.note) {
                <span class="text-secondary text-sm">
                    {{ tenantConfig.getConfig().footerLinks.note }}
                </span>
            } @else {
                <span class="text-secondary text-sm"
                    >🇮🇳 Proudly Made in India</span
                >
            }
        </div>
    </div>
</div>

<!-- Quick chat -->
<!-- <quick-chat #quickChat="quickChat"></quick-chat> -->
